import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import AdminTable from '../../../components/AdminTable';
import DashboardHeaderWithFilters from '../../../components/DashboardHeaderWithFilters';
import EmptyRecord from '../../../components/EmptyRecord';
import useFilterMethods from '../../../customHooks/useFilterMethods';
import useGetQueryParams from '../../../customHooks/useGetQueryParams';
import useTableColumn from '../../../customHooks/useTableColumn';
import useToast from '../../../customHooks/useToast';
import { GET_USER_REFEREES } from '../../../graphql/admin_queries';
import useCustomQuery from '../../../network/useCustomQuery';
import EmptyMerchantSettlement from '../../../svgs/EmptyMerchantSettlement';
import generateCSV from '../../../utils/generateCSV';
import generatePDF from '../../../utils/generatePdf';
import { cleanUpRewardDetails } from '../../../utils/helpers';
import Styles from './RewardDetailsTableRecord.module.scss';

const limit = 14;
interface IRewardDetailsTableRecord{
  referralId:string;
}

const RewardDetailsTableRecord = ({ referralId }:IRewardDetailsTableRecord) => {
  const Toast = useToast();
  const { userId } = useParams<{ userId: string }>();
  const [getUserReferees, userRefereesLoading, userReferees] = useCustomQuery({
    query: GET_USER_REFEREES,
    onError: () => {},
    onData: () => {},
  });
  const { getQuery, getFilterQueries } = useGetQueryParams();
  const search = getQuery('search') || '';
  const page = Number(getQuery('page'));
  const { push } = useHistory();

  const allColumns = [
    ['image', 'email', 'hasBottomValue', 'name'],
    'status',
    'location',
    'onboardingStatus',
    'enrollmentType',
    'earning',
    // 'referralCode',
    // 'completedReferrals',
  ];
  const columnLabelFormat = {
    name: 'Customer',
    enrollmentType: 'User Type',
    location: 'Location',
    totalReferred: 'Total Referred',
    onboardingStatus: 'Onboarding Status',
    earning: 'Earnings',
    referralCode: 'Referral Code',
    completedReferrals: 'Completed Referrals',
  };
  const { getColumns } = useTableColumn(allColumns, 'reward');

  const [trxRec, setrxRec] = useState(null);
  const setPage = (pageNum) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', pageNum);
    push({
      search: searchParams.toString(),
    });
  };
  if (!page) {
    push(`/rewards/rewards-details/${userId}?page=1&search=${search}`);
  }

  const onSearchChange = (e) => {
    const result = getFilterQueries();
    push(`/rewards/rewards-details/${userId}?page=1&search=${encodeURIComponent(e.target.value)}&fromDateTable=${result?.fromDateTable}&toDateTable=${result?.toDateTable}`);
  };

  useEffect(() => {
    if (referralId && page) {
      const result = getFilterQueries();
      getUserReferees({
        variables: {
          offset: (page - 1) * limit,
          limit,
          referralDetailsId: referralId,
          // ...(tabStatus ? { statusFilter: tabStatus } : {}),
          search,
          filters: {
            from: result.fromDateTable,
            to: result.toDateTable,
          },
        },
      });
    }
  }, [page, search, referralId]);

  const getRewardFunc = (result) => {
    getUserReferees({
      variables: {
        offset: 0,
        limit,
        search,
        referralDetailsId: referralId,
        filters: {
          from: result.fromDateTable,
          to: result.toDateTable,
          // ...(tabStatus ? {} : { status: result.status }),

        },
      },
    });
  };

  const onFilterSubmit = (result) => {
    if (referralId) {
      getRewardFunc(result);
    }
  };

  const {
    filterObj,
  } = useFilterMethods({
    allowedFilters: ['tableDateRange'],
    defaultUrl: `/rewards/rewards-details/${userId}?page=1`,
    cb: onFilterSubmit,
    Toast,
  });

  useEffect(() => {
    if (userReferees?.getUserReferees?.referees) {
      setrxRec(userReferees?.getUserReferees);
    }
  }, [userReferees]);

  const [getExportRecord] = useCustomQuery({
    query: GET_USER_REFEREES,
    onError: () => {},
    onData: () => {},
  });

  const handleExport = async (type: string) => {
    try {
      const result = getFilterQueries();
      const output = await getExportRecord({
        variables: {
          offset: 0,
          limit: 2000,
          referralDetailsId: referralId,
          // ...(tabStatus ? { statusFilter: tabStatus } : {}),
          search,
          filter: {
            from: result.fromDateTable,
            to: result.toDateTable,
            // status: result.status,
            // minAmount: result.min ? Number(result.min) : 0,
            // maxAmount: result.max ? Number(result.max) : 0,
          },
        },
      });

      if (output?.data?.getUserReferees) {
        const rec = cleanUpRewardDetails(output?.data?.getUserReferees?.referees);
        const func = type === 'pdf' ? generatePDF : generateCSV;
        const selectedHeaders = [
          'S/N',
          // change image__email__hasBottomValue__name concated columns to a single column of name
          ...(getColumns?.()?.map((column) => (column === 'image__email__hasBottomValue__name' ? ['name', 'email'] : column)) || []),
        ];

        func({
          payload: rec,
          title: 'Rewards',
          selectedHeaders,
          formatHeaders: columnLabelFormat,
          cb: () => Toast.success('Exported successfully'),
        });
      }
    } catch (error) {
      Toast.error('Error while exporting');
    }
  };

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.wrapper__header}>
        <DashboardHeaderWithFilters
          title="Referral statistics"
          filterObj={filterObj}
          dateType="tableDateRange"
          showSearch
          search={search}
          onSearchChange={onSearchChange}
          searchPlaceholder="Customer name /  Email address"
          exportObj={{
            handleExport,
          }}
        />
      </div>

      <div className="transaction-table-details-cover">
        {trxRec?.count ? (
          <AdminTable
            refreshing={userRefereesLoading}
            page={page}
            setPage={setPage}
            pageSize={limit}
            minHeight="calc(100vh - 280px)"
            count={trxRec?.count}
            contents={cleanUpRewardDetails(trxRec?.referees)}
            reference="id"
            hideDominantImage
            hasNameAndImage
            allowKeyArr={getColumns(true)}
            mobileAllowKeyArr={
              [
                'status',
                ['name', 'email'],
                ['earning', 'location'],
              ]
            }
            formatLabels={columnLabelFormat}
          />
        ) : (
          <EmptyRecord
            height="calc(100vh - 240px)"
            title={userRefereesLoading ? 'Fetching records...' : 'No records yet'}
            main="All Rewards from customers on your Payfi will appear here"
            // footer="Receive Payments"
            loading={userRefereesLoading}
            Icon={EmptyMerchantSettlement}
          />
        )}

      </div>
    </div>
  );
};

export default RewardDetailsTableRecord;
